import React, { useState } from 'react';

export default function BookingForm() {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    dates: '',
    service: 'lease',
    notes: ''
  });
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  // Anti-bot measures
  const [honeypot, setHoneypot] = useState('');
  const [formLoadTime] = useState(Date.now());

  const validateForm = () => {
    const newErrors = {};

    // Name validation
    if (!formData.name.trim()) {
      newErrors.name = 'Vyplňte jméno';
    } else if (formData.name.trim().length < 2) {
      newErrors.name = 'Jméno musí mít alespoň 2 znaky';
    }

    // Phone validation - Czech phone format
    const phoneRegex = /^(\+420)?[0-9]{9}$/;
    const cleanPhone = formData.phone.trim().replace(/\s/g, '');
    if (!formData.phone.trim()) {
      newErrors.phone = 'Vyplňte telefon';
    } else if (!phoneRegex.test(cleanPhone)) {
      newErrors.phone = 'Vyplňte platné telefonní číslo (např. +420123456789 nebo 123456789)';
    }

    // Email validation - proper regex
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!formData.email.trim()) {
      newErrors.email = 'Vyplňte email';
    } else if (!emailRegex.test(formData.email.trim())) {
      newErrors.email = 'Vyplňte platný email';
    }

    // Dates validation
    if (!formData.dates.trim()) {
      newErrors.dates = 'Vyplňte termíny';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    // Anti-bot checks
    // 1. Honeypot field check - bots often fill all fields
    if (honeypot) {
      setErrors({ submit: 'Váš formulář nelze odeslat. Zkuste to prosím později.' });
      return;
    }

    // 2. Time-based check - form must be open for at least 3 seconds
    const timeSinceLoad = Date.now() - formLoadTime;
    if (timeSinceLoad < 3000) {
      setErrors({ submit: 'Formulář byl odeslán příliš rychle. Počkejte chvíli a zkuste to znovu.' });
      return;
    }

    setIsLoading(true);

    try {
      const response = await fetch('/api/send-booking', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...formData,
          _hp: honeypot, // Send honeypot for server-side validation
        }),
      });

      const data = await response.json();

      if (response.ok) {
        // Show confirmation
        setSubmitted(true);

        // Reset form after 5 seconds
        setTimeout(() => {
          setSubmitted(false);
          setFormData({
            name: '',
            phone: '',
            email: '',
            dates: '',
            service: 'lease',
            notes: ''
          });
          setErrors({});
        }, 5000);
      } else {
        console.error('Failed to submit booking:', data);
        const errorMessage = data.error || 'Něco se pokazilo. Zkuste to prosím znovu.';
        setErrors({ submit: errorMessage });
      }
    } catch (error) {
      console.error('Error submitting booking:', error);
      setErrors({ submit: 'Nepodařilo se spojit se serverem. Zkontrolujte připojení k internetu.' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  return (
    <section className="bg-white rounded-lg shadow-md p-4 sm:p-6 md:p-8 mb-8" aria-labelledby="booking-heading">
      <h2 id="booking-heading" className="text-2xl sm:text-3xl font-bold text-stone-800 mb-4 sm:mb-6">Máte zájem?</h2>

      {submitted ? (
        <div className="bg-green-100 border-2 border-green-500 rounded-lg p-4 sm:p-6 text-center" role="alert" aria-live="polite">
          <h3 className="text-xl sm:text-2xl font-bold text-green-800 mb-2">Super, máme to! 👍</h3>
          <p className="text-sm sm:text-base text-green-700">
            Potvrzení jsme Vám poslali na email. Ozveme se brzy a domluvíme detaily!
          </p>
        </div>
      ) : (
        <form onSubmit={(e) => { e.preventDefault(); handleSubmit(); }} className="space-y-4 sm:space-y-6">
          <p className="text-sm sm:text-base text-stone-600">
            Vyplňte formulář a ozveme se Vám. Případně se můžete zeptat na cokoliv v poznámce.
          </p>

          <div>
            <label htmlFor="name-input" className="block text-stone-700 font-semibold mb-2 text-sm sm:text-base">Vaše jméno *</label>
            <input
              type="text"
              id="name-input"
              name="name"
              value={formData.name}
              onChange={handleChange}
              className="w-full px-3 py-2.5 sm:px-4 sm:py-3 border-2 border-stone-300 rounded-lg focus:border-amber-500 focus:outline-none text-sm sm:text-base"
              placeholder="Jan Novák"
              required
              aria-required="true"
              aria-invalid={errors.name ? 'true' : 'false'}
              aria-describedby={errors.name ? 'name-error' : undefined}
            />
            {errors.name && <p id="name-error" className="text-red-600 text-xs sm:text-sm mt-1" role="alert">{errors.name}</p>}
          </div>

          <div className="grid md:grid-cols-2 gap-4 sm:gap-6">
            <div>
              <label htmlFor="phone-input" className="block text-stone-700 font-semibold mb-2 text-sm sm:text-base">Telefon *</label>
              <input
                type="tel"
                id="phone-input"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                className="w-full px-3 py-2.5 sm:px-4 sm:py-3 border-2 border-stone-300 rounded-lg focus:border-amber-500 focus:outline-none text-sm sm:text-base"
                placeholder="+420 123 456 789"
                required
                aria-required="true"
                aria-invalid={errors.phone ? 'true' : 'false'}
                aria-describedby={errors.phone ? 'phone-error' : undefined}
              />
              {errors.phone && <p id="phone-error" className="text-red-600 text-xs sm:text-sm mt-1" role="alert">{errors.phone}</p>}
            </div>

            <div>
              <label htmlFor="email-input" className="block text-stone-700 font-semibold mb-2 text-sm sm:text-base">Email *</label>
              <input
                type="email"
                id="email-input"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className="w-full px-3 py-2.5 sm:px-4 sm:py-3 border-2 border-stone-300 rounded-lg focus:border-amber-500 focus:outline-none text-sm sm:text-base"
                placeholder="jan.novak@email.cz"
                required
                aria-required="true"
                aria-invalid={errors.email ? 'true' : 'false'}
                aria-describedby={errors.email ? 'email-error' : undefined}
              />
              {errors.email && <p id="email-error" className="text-red-600 text-xs sm:text-sm mt-1" role="alert">{errors.email}</p>}
            </div>
          </div>

          <div>
            <label htmlFor="service-select" className="block text-stone-700 font-semibold mb-2 text-sm sm:text-base">Co Vás zajímá? *</label>
            <select
              id="service-select"
              name="service"
              value={formData.service}
              onChange={handleChange}
              className="w-full px-3 py-2.5 sm:px-4 sm:py-3 border-2 border-stone-300 rounded-lg focus:border-amber-500 focus:outline-none text-sm sm:text-base"
              required
              aria-required="true"
            >
              <option value="lease">Půjčit tromlovačku (250 Kč/týden)</option>
              <option value="tumbling">Tromlovat kameny za mě (podle množství)</option>
            </select>
          </div>

          <div>
            <label htmlFor="dates-input" className="block text-stone-700 font-semibold mb-2 text-sm sm:text-base">
              Kdy by Vám to vyhovovalo? *
            </label>
            <input
              type="text"
              id="dates-input"
              name="dates"
              value={formData.dates}
              onChange={handleChange}
              className="w-full px-3 py-2.5 sm:px-4 sm:py-3 border-2 border-stone-300 rounded-lg focus:border-amber-500 focus:outline-none text-sm sm:text-base"
              placeholder="např. 10.1. - 17.1. nebo kdykoliv v lednu"
              required
              aria-required="true"
              aria-invalid={errors.dates ? 'true' : 'false'}
              aria-describedby={errors.dates ? 'dates-error' : undefined}
            />
            {errors.dates && <p id="dates-error" className="text-red-600 text-xs sm:text-sm mt-1" role="alert">{errors.dates}</p>}
          </div>

          <div>
            <label htmlFor="notes-textarea" className="block text-stone-700 font-semibold mb-2 text-sm sm:text-base">Něco dalšího?</label>
            <textarea
              id="notes-textarea"
              name="notes"
              value={formData.notes}
              onChange={handleChange}
              rows={4}
              className="w-full px-3 py-2.5 sm:px-4 sm:py-3 border-2 border-stone-300 rounded-lg focus:border-amber-500 focus:outline-none text-sm sm:text-base"
              placeholder="Ptejte se na co chcete... kolik máte kamenů, jaký typ, jestli máte vlastní grity..."
              maxLength={1000}
            />
          </div>

          {/* Honeypot field - hidden from real users, bots will likely fill it */}
          <input
            type="text"
            name="website"
            value={honeypot}
            onChange={(e) => setHoneypot(e.target.value)}
            autoComplete="off"
            tabIndex={-1}
            aria-hidden="true"
            style={{
              position: 'absolute',
              left: '-9999px',
              width: '1px',
              height: '1px',
              opacity: 0,
              pointerEvents: 'none'
            }}
          />

          {errors.submit && (
            <div className="bg-red-100 border-2 border-red-500 rounded-lg p-3 sm:p-4 text-center" role="alert" aria-live="assertive">
              <p className="text-sm sm:text-base text-red-700">{errors.submit}</p>
            </div>
          )}

          <button
            type="submit"
            disabled={isLoading}
            className="w-full bg-gradient-to-r from-amber-600 to-orange-500 hover:from-amber-700 hover:to-orange-600 text-white font-bold py-3 sm:py-4 px-4 sm:px-6 rounded-lg text-base sm:text-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed min-h-[48px]"
            aria-busy={isLoading}
          >
            {isLoading ? 'Odesílám...' : 'Poslat rezervaci'}
          </button>
        </form>
      )}
    </section>
  );
}
